import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

import { verifyAccessCode } from 'utility/requests';
import { useNotification } from 'utility/notification';
import { Trans, t } from '@lingui/macro';

export default function AccessCodeForm(props) {
    const { eventId, accessCodes, onSubmit } = props;

    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { createNotification } = useNotification();

    const handleSubmit = () => {
        const used = accessCodes.some((c) => c.toLowerCase() === code.toLowerCase());
        if (used === true) {
            setError(t`This code has already been used`);
            return;
        }

        setLoading(true);

        verifyAccessCode(code, eventId)
            .then((ac) => {
                if (ac.valid === true) {
                    onSubmit(code);
                    setError(null);
                    createNotification(t`Access Code Applied`, { variant: 'success' });
                } else {
                    setError(t`This code is not valid`);
                }
            })
            .catch(() => {
                setError(t`There was a problem applying your access code`);
            })
            .finally(() => setLoading(false));
    };

    const handleFormChange = (e) => {
        const value = e.target.value.replace(/ /g, '').slice(0, 50);
        setCode(value);
        setError(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    const disabledUnlock = loading || code.length === 0;

    return (
        <div className="flex w-full justify-center px-4 py-4 md:px-8">
            <Card className="mb-4 w-full max-w-xl border-solid border-border bg-card shadow-none rounded-xl">
                <CardContent className="space-y-1 p-4 sm:p-6 sm:pt-6">
                    <h2 className="text-lg font-bold font-inter text-foreground mt-0">
                        <Trans>Unlock Hidden Tickets</Trans>
                    </h2>
                    <p className="text-sm text-foreground">
                        <Trans>Enter your Access Code here.</Trans>
                    </p>
                    <p className="text-xs text-muted-foreground">
                        (<Trans>discount codes are entered at checkout.</Trans>)
                    </p>
                    <div className="mt-4 space-y-1.5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="min-w-0 flex-1">
                                <label htmlFor="access-code" className="sr-only">
                                    <Trans>Access code</Trans>
                                </label>
                                <Input
                                    id="access-code"
                                    type="text"
                                    autoComplete="off"
                                    autoCapitalize="none"
                                    spellCheck={false}
                                    value={code}
                                    disabled={loading}
                                    onChange={handleFormChange}
                                    onKeyDown={handleKeyDown}
                                    className={cn(
                                        // Match landing SearchBar surface (LandingPage)
                                        'box-border !h-[46px] border border-solid py-0 font-inter leading-normal text-foreground shadow-none focus:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        error &&
                                        'border-destructive focus:border-destructive',
                                    )}
                                    aria-invalid={Boolean(error)}
                                    aria-describedby={error ? 'access-code-error' : undefined}
                                />
                            </div>
                            <Button
                                type="button"
                                variant="default"
                                disabled={disabledUnlock}
                                onClick={handleSubmit}
                                className="box-border h-[46px] w-full shrink-0 px-6 font-bold uppercase tracking-wide sm:w-[140px] sm:shrink-0"
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                ) : null}
                                <Trans>Unlock</Trans>
                            </Button>
                        </div>
                        {error ? (
                            <p
                                id="access-code-error"
                                className="text-sm text-destructive"
                                role="alert"
                            >
                                {error}
                            </p>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
